import { AnalyticsUtils } from '@analytics/utils';
import type { OnInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';
import type {
  ParsedToolTrackingConfig,
  TrackedToolComponent,
} from '@analytics/models';
import {
  TrackImpressionDirective,
  TrackStepDirective,
  TrackToolService,
} from '@analytics/events';
import { GenericRichTextDirective, getFieldValue } from '@core/jss-utils';
import {
  ComposableCardComponent,
  ComposableCardConfig,
  getComposableCardConfigFromRendering,
} from '@legacy/common-ui-common-renderings';
import type { ScFormFormGroupState } from '@innogy/sitecore-forms/models';
import { GenericFormRendering } from '@innogy/sitecore-forms/models';
import { GenericFormService } from '@innogy/sitecore-forms/services';
import type { ScFormState } from '@innogy/sitecore-forms/store';
import {
  parseGenericFormToolTrackingConfig,
  submitGenericFormAction,
} from '@innogy/sitecore-forms/store';
import type { ContentContext } from '@legacy/common-ui-partials';
import { Store } from '@ngrx/store';
import type { FormGroupState } from 'ngrx-forms';
import type { Observable } from 'rxjs';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { InfoCardComponent } from '@legacy/common-ui-utility-components';
import { AsyncPipe } from '@angular/common';
import {
  ContentSectionComponent,
  type ContentSectionTheme,
  getContentSectionId,
  getContentSectionTheme,
} from '@core/content-section';
import { ColumnContext } from '@legacy/common-ui-shared-interfaces';

import { GenericFormComponent } from '../generic-form/generic-form.component';

@Component({
  selector: 'wl-generic-form-container[rendering]',
  templateUrl: './generic-form-container.component.html',
  standalone: true,
  imports: [
    ComposableCardComponent,
    JssModule,
    TrackStepDirective,
    GenericFormComponent,
    TrackImpressionDirective,
    AutomationIdDirective,
    GenericRichTextDirective,
    InfoCardComponent,
    AsyncPipe,
    ContentSectionComponent,
  ],
})
export class GenericFormContainerComponent
  implements OnInit, TrackedToolComponent<'form'>
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: GenericFormRendering;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context?: ColumnContext;

  id?: string;
  theme?: ContentSectionTheme;

  viewModel$?: Observable<ScFormState>;
  cardConfig?: ComposableCardConfig;

  trackToolConfig: ParsedToolTrackingConfig<'form'> | undefined = undefined;
  e2eTrackingId = inject(AnalyticsUtils).generateAnalyticsE2EId();

  contentContext: ContentContext = 'default';

  private initializeForm() {
    const form = this.rendering.fields.Form;

    this.genericFormService.registerForm(form);
    const { selector: formStateSelector, asObservable: viewModel } =
      this.genericFormService.selectFormStateForForm(form.id);

    this.viewModel$ = viewModel;
    this.trackToolConfig = parseGenericFormToolTrackingConfig(
      form,
      formStateSelector,
      this.e2eTrackingId
    );
  }

  ngOnInit() {
    this.initializeForm();

    this.cardConfig = getComposableCardConfigFromRendering(this.rendering);

    this.contentContext =
      this.rendering.fields.Form.fields.Context?.fields.value?.value ||
      'default';

    this.id = getContentSectionId(this.rendering);
    this.theme = getContentSectionTheme(this.rendering);
  }

  submit(event: Event, formGroupState: FormGroupState<ScFormFormGroupState>) {
    event.preventDefault();

    const integrationSettingsPath =
      this.rendering.fields.Form.fields.Integration?.value ?? '';

    this.store$.dispatch(
      submitGenericFormAction({
        formId: this.rendering.fields.Form.id,
        formGroupState,
        integrationSettingsPath,
        e2eTrackingId: this.e2eTrackingId,
      })
    );

    if (formGroupState.isValid && this.trackToolConfig) {
      this.trackToolService.completeTool(this.trackToolConfig);
    }
  }

  displayForm(vm: ScFormState) {
    return (
      (!vm.feedback?.isPosted || !!vm.feedback.redirectOnSuccessPage?.href) &&
      Boolean(vm.form.formState)
    );
  }

  displaySubmissionSucceededText(vm: ScFormState) {
    return (
      vm.feedback?.isPosted &&
      !vm.feedback.redirectOnSuccessPage?.href &&
      !!getFieldValue(this.rendering, 'SubmissionSucceededText')
    );
  }

  displaySubmissionFailedText(vm: ScFormState) {
    return (
      vm.feedback?.submitError &&
      !!getFieldValue(this.rendering, 'SubmissionFailedMessage')
    );
  }

  constructor(
    private readonly store$: Store,
    private readonly genericFormService: GenericFormService,
    private readonly trackToolService: TrackToolService
  ) {}
}
